
import {
    type GridColDef, GridActionsCellItem, type GridSortModel
} from '@mui/x-data-grid-pro';

import { Box, Typography, Chip, Tooltip } from '@mui/material';
import { MtComStyleObj } from '../styles/MtComStyleObj';
import { useGlobalState } from "../contexts/GlobalState";
import { black } from '../colors';
import { createRenderColumnHeader } from '../components/common/GridHeaderUtils';
import delete_icon_red from '/icons/delete_icon_red.png';
import { applyDashValueFormatter, formatMissingText } from './missingValue';

export function getUserManagementCols(deleteUserConfirm: any, editUserBindData: any, classes: any, roleSelected: string, sortModel: GridSortModel = []): GridColDef[] {
    const renderHeader = createRenderColumnHeader(sortModel);
    const { state: globalState } = useGlobalState();
    const columns: GridColDef[] = [
        {
            field: 'user', headerName: 'User Name', width: 400, hideSortIcons: false,
            renderHeader,
            valueGetter: (_, params: any) => {
                return params?.user_name ?? "";
            },

            renderCell: (params: any) => {
                return (
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                        }}>
                        <Box
                            sx={{
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                background: "#CEFFDD",
                                verticalAlign: "middle",
                                color: "#14803C",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12
                            }}>
                            {...params?.row?.initial}
                        </Box>

                        <Typography component="span" sx={{ color: 'text.secondary', fontSize: 14, mx: 1 }}>
                            <Typography component="p" color={black} className={classes.labelNoto16Bold400}>{formatMissingText(params?.row?.user_name)}</Typography>
                            <Typography component="p" className={classes.labelNoto14Bold400}>{formatMissingText(params?.row?.user_email)}</Typography>
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'roles', headerName: 'Role', flex: 1, minWidth: 160,
            renderHeader,
            valueGetter: (params: any) => {
                const roles = params;
                if (!Array.isArray(roles)) return '';
                return roles?.join(",")
            },

            renderCell: (params: any) => {
                const roles = params?.row?.roles ?? [];
                return (
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            width: "100%",
                            gap: 0.5
                        }}>

                        {roles?.length > 0 ? roles?.map((role: any) => (
                            <Chip label={role} variant="outlined" size="small" sx={{ mt: 1, borderRadius: "8px" }} className={classes.labelNoto14Bold400}
                                onClick={(e) => {
                                    e.stopPropagation();

                                }}
                            />
                        )) : <Typography className={classes.labelNoto14Bold400} sx={{ fontSize: 16, fontWeight: 600 }}>—</Typography>}
                    </Box>
                )
            },

        },
        {
            field: 'modules',
            headerName: 'Module',
            width: 190,
            type: 'singleSelect',
            renderHeader,
            valueGetter: (params: any) => {
                const modules = params;
                if (!Array.isArray(modules)) return '';
                return modules?.join(",")
            },
            renderCell: (params: any) => {
                const modules = params?.row?.modules ?? [];
                return (
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            width: "100%",
                            gap: 0.5
                        }}>
                        {modules.length > 0 ? modules.map((module: any) => (
                            <Chip label={module} variant="outlined" size="small" sx={{ mt: 1, borderRadius: "8px" }} className={classes.labelNoto14Bold400}
                                onClick={(e) => {
                                    e.stopPropagation();

                                }}
                            />
                        )) : <Typography className={classes.labelNoto14Bold400} sx={{ fontSize: 16, fontWeight: 600 }}>—</Typography>}
                    </Box>
                )
            }
        },
        {
            field: 'sites', headerName: 'Site name', flex: 1, minWidth: 220,
            renderHeader,
            valueGetter: (params: any) => {
                const sites = params;
                if (!Array.isArray(sites)) return '';
                return sites?.join(",")
            },
            renderCell: (params: any) => {
                const sites = params?.row?.sites ?? [];
                return (
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            width: "100%",
                            gap: 0.5
                        }}
                    >
                        {sites.length > 0 ? sites.map((site: any) => (
                            <Chip label={site} variant="outlined" size="small" sx={{ mt: 1, borderRadius: "8px" }} className={classes.labelNoto14Bold400}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            />
                        )) : <Typography className={classes.labelNoto14Bold400} sx={{ fontSize: 16, fontWeight: 600 }}>—</Typography>}
                    </Box>
                )
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            type: "actions",
            sortable:false,
            getActions: (params) => {
                let actions: any[] = [];
                let editData = <Tooltip key="edit" title="Edit user" arrow placement="top">
                    <GridActionsCellItem
                        icon={<img
                            src={"icons/Edit.svg"}
                            alt="Save"
                            width={20}
                            height={20}
                            style={{ display: "block" }}
                        />}
                        label="Edit"
                        disableRipple
                        disableFocusRipple
                        sx={(theme) => ({
                            ...MtComStyleObj(theme).gridIconHoverStyle,   boxShadow:"none"
                        })}
                        color="inherit"
                        onClick={(event) => {
                            event.stopPropagation();
                            editUserBindData(params);
                        }}
                    />
                </Tooltip>;
                let deleteData = <Tooltip key="delete" title="Delete user" arrow placement="top">
                    <GridActionsCellItem
                        icon={<img src={delete_icon_red} alt="Delete" width={16} height={16} />}
                        sx={(theme) => ({
                            ...MtComStyleObj(theme).gridIconHoverStyle,
                            boxShadow:"none"
                        })}
                        label="Delete"
                        color="inherit"
                        onClick={(event) => {
                            event.stopPropagation();
                            deleteUserConfirm({ "user_id": params?.row?.user_id ?? "", "deleted_by": globalState?.user_id_db });
                        }} />
                </Tooltip>
                if (["Admin","S2S-Lead"].includes(roleSelected)) {
                    actions = [editData, deleteData]
                } else {
                    // Breeder/Technician still see the column, greyed out and not clickable.
                    actions = [
                        <Tooltip key="no-access-edit" title="Action not accessible" arrow placement="top">
                            <span>
                                <GridActionsCellItem
                                    icon={<img src={"icons/Edit.svg"} alt="Edit" width={20} height={20} style={{ display: "block", opacity: 0.4 }} />}
                                    label="Edit"
                                    disabled
                                />
                            </span>
                        </Tooltip>,
                        <Tooltip key="no-access-delete" title="Action not accessible" arrow placement="top">
                            <span>
                                <GridActionsCellItem
                                    icon={<img src={delete_icon_red} alt="Delete" width={16} height={16} style={{ opacity: 0.4 }} />}
                                    label="Delete"
                                    disabled
                                />
                            </span>
                        </Tooltip>
                    ]
                }
                return actions;
            },

        }

    ];

    return applyDashValueFormatter(columns);
}




